/**
 * This set of hooks is how we'll interact with the organizationStore. The idea is to provide a simple api to get what
 * we need from the store without having to use `dispatch`, `connect`, `mapStoreToProps`, and all that stuff
 * in the components.
 */
import React, { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux'
import apiUtils from '../../global/utils/api';
import { parseQueryArgs, selectSingleById } from '../../global/utils/storeUtils';

// import all of the actions from the store
import {
  fetchOrganizationList
  , fetchOrganizationListAtEndpoint
  , fetchListIfNeeded
  , fetchSingleOrganization
  , fetchSingleOrganizationAtEndpoint
  , sendCreateOrganization
  , sendUpdateOrganization
  , sendDeleteOrganization
  , invalidateQuery
  // , invalidateQueries
  , addOrganizationToList
  , fetchSingleIfNeeded
  , myOrganizationsEndpoint
} from './organizationStore';
import {
  useGetResourceById
  , useGetResource
  , useGetResourceList
  , useMutateResource
} from '../../global/utils/serviceHooks';
import { useGetGroupList } from '../group/groupService';
import { myGroupsEndpoint } from '../group/groupStore';
import { useGetGroupUserList } from '../groupUser/groupUserService';
import { myGroupUsersEndpoint } from '../groupUser/groupUserStore';
import { useLoggedInUser } from '../user/authService';


// Define the hooks that we'll use to manage data in the components

// CREATE

/**
 * Use this hook to handle the creation of a new organization.
 * @param {Object} initialState - The initial state of the organization (optional)
 * @param {Function} handleResponse - The function to call when the organization is successfully created
 * 
 * @returns an object containing fetch info and the following:
 * - `newOrganization` as `data`: the new organization object as it currently exists in state, initially the default organization
 * - `handleChange`: standard form change handler to be used in the form
 * - `handleSubmit`: standard form submit handler to be used in the form
 * - `setFormState`: a way to handle form state changes in the component instead of `handleChange`, rarely needed but sometimes necessary
 * @example // to use in a component
 * // access the create action and fetch the default organization
 * const { data: newOrganization, handleChange, handleSubmit, ...organizationQuery } = useCreateOrganization({
 *   // optional, anything we want to add to the default object
 *   initialState: {
 *     someKey: 'someValue'
 *   }
 *   // optional, callback function that receives the response from the server
 *   , handleResponse: (organization, error) => {
 *     if(error || !organization) {
 *       alert(error || "An error occurred.")
 *     }
 *     history.push(`/organizations/${organization._id}`)
 *   }
 * });
 * 
 * return (
 *   <WaitOn query={organizationQuery}>
 *     <OrganizationForm
 *       organization={organization}
 *       handleSubmit={handleSubmit}
 *       handleChange={handleChange}
 *     />
 *   </WaitOn>
 * )
 */
export const useCreateOrganization = ({ initialState = {}, onResponse = () => { } }) => {
  const dispatch = useDispatch();
  // set up organization specific stuff to be used by the shared hook
  const defaultOrganizationQuery = useGetDefaultOrganization();
  const sendMutation = (mutatedOrganization) => dispatch(sendCreateOrganization(mutatedOrganization));

  // the hook will return everything the caller needs to create a new organization
  return useMutateResource({ resourceQuery: defaultOrganizationQuery, sendMutation, initialState, onResponse, isCreate: true });
}

// READ

/**
 * NOTE: If you are using this because you want to create a new organization, try `useCreateOrganization`
 * instead. It handles everything for you!
 * 
 * @param {boolean} forceFetch - optional override to force a fetch from the server
 * @returns an object containing fetch info and eventually the defaultOrganization (as `data`)
 * @returns a refetch function for convenience (will probably never be used for default organization, but keeps things consistent)
 */
export const useGetDefaultOrganization = (forceFetch = false) => {
  // leverage existing hooks to get the default organization (using 'default' as the id will return the default organization from the server)
  return useGetOrganizationById('default', forceFetch);
}


/**
 * This hook will check for a fresh organization in the store and fetch a new one if necessary
 * 
 * @param {string} id - the id of the organization to be fetched
 * @param {boolean} forceFetch - optional override to force a fetch from the server
 * @returns an object containing fetch info and eventually the organization (as `data`)
 * @returns an invalidate and refetch function for convenience
 */
export const useGetOrganizationById = (id, forceFetch = false) => {
  const dispatch = useDispatch();
  // set up organization specific stuff to be used by the shared hook
  const organizationStore = useSelector(({ organization }) => organization);
  const fetchOrganization = forceFetch ? fetchSingleOrganization : fetchSingleIfNeeded;
  const sendFetchById = (id) => dispatch(fetchOrganization(id));
  const sendInvalidateSingle = (id) => dispatch(invalidateQuery(id));

  // return the (now organization specific) hook
  return useGetResourceById({ id, fromStore: organizationStore, sendFetchById, sendInvalidateSingle });

}

/**
 * This hook will check for a fresh organization in the store and fetch a new one if necessary
 * 
 * @param {...string | object | null} args - accepts two optional arguments: a string (endpoint) or an object (listArgs) or both as (endpoint, listArgs)
 * @returns an object containing fetch info and eventually the organization (as `data`)
 * @returns an invalidate and refetch function for convenience
 */
export const useGetOrganization = (...args) => {
  const dispatch = useDispatch();
  const { endpoint, listArgs } = parseQueryArgs(args);
  // set up organization specific stuff to be used by the shared hook
  const organizationStore = useSelector(({ organization }) => organization);
  const fetchOrganization = endpoint ? fetchSingleOrganizationAtEndpoint : fetchSingleOrganization;
  // finally package up the actions to be used by the shared hook
  const sendFetchSingle = (queryString) => dispatch(fetchSingleIfNeeded(queryString, fetchOrganization));
  const sendInvalidateSingle = (queryString) => dispatch(invalidateQuery(queryString));
  // return the (now organization specific) hook
  return useGetResource({ listArgs, fromStore: organizationStore, sendFetchSingle, sendInvalidateSingle, endpoint });
}

/**
 * This hook will check for a fresh list in the store and fetch a new one if necessary
 * 
 * @param {...string | object | null} args - accepts two optional arguments: a string (endpoint) or an object (listArgs) or both as (endpoint, listArgs)
 * @returns an object containing fetch info and eventually the organization list (as `data`)
 * @returns an invalidate and refetch function for convenience
 */
export const useGetOrganizationList = (...args) => {
  const dispatch = useDispatch();
  const { endpoint, listArgs } = parseQueryArgs(args);
  // set up organization specific stuff to be used by the shared hook
  const organizationStore = useSelector(({ organization }) => organization);
  // if an endpoint was passed in, use that, otherwise use the default
  const fetchOrganizations = endpoint ? fetchOrganizationListAtEndpoint : fetchOrganizationList;
  const sendFetchList = (queryString) => dispatch(fetchListIfNeeded(queryString, fetchOrganizations));
  const sendInvalidateList = (queryString) => dispatch(invalidateQuery(queryString));

  // return the (now organization specific) hook
  return useGetResourceList({ listArgs, fromStore: organizationStore, sendFetchList, sendInvalidateList, endpoint });
}

// UPDATE

/**
 * Use this hook to access the `sendUpdateOrganization` action
 * 
 * Useful if you want to update an organization that you already have access to
 * 
 * NOTE: Check out `useGetUpdatableOrganization` if you want to fetch and update an organization
 * 
 * @returns the sendUpdateOrganization action wrapped in dispatch
 * @example // to use in a component
 * // access the update action
 * const { sendUpdateOrganization } = useUpdateOrganization();
 * // dispatch the update action
 * sendUpdateOrganization(updatedOrganization);
 */
export const useUpdateOrganization = () => {
  const dispatch = useDispatch();
  return {
    // return the update action
    sendUpdateOrganization: (updatedOrganization) => dispatch(sendUpdateOrganization(updatedOrganization))
  }
}

/**
 * Use this hook to handle the update of an existing organization.
 * @param {string} id - the id of the organization to be updated.
 * @param {Object} options - an object that expects an optional onResponse function that receives the updated organization and error.
 * 
 * @returns an object containing fetch info and the following:
 * - `organization` as `data`: the organization object as it currently exists in state
 * - `handleChange`: standard form change handler to be used in the form
 * - `handleSubmit`: standard form submit handler to be used in the form
 * - `setFormState`: a way to handle form state changes in the component instead of `handleChange`, rarely needed but sometimes necessary
 * @example // to use in a component
 * // fetch the organization and access everything needed to handle updating it
 * const { data: organization, handleChange, handleSubmit, ...organizationQuery } = useGetUpdatableOrganization(organizationId, {
 *   // optional, callback function to run after the request is complete
 *   onResponse: (updatedOrganization, error) => {
 *     if(error || !updatedOrganization) {
 *       alert(error || "An error occurred.")
 *     }
 *     history.push(`/organizations/${organizationId}`)
 *   }
 * });
 * 
 * return (
 *   <WaitOn query={organizationQuery}>
 *     <OrganizationForm
 *       organization={organization}
 *       handleSubmit={handleSubmit}
 *       handleChange={handleChange}
 *     />
 *   </WaitOn>
 * )
 */
export const useGetUpdatableOrganization = (id, { onResponse = () => { } } = {}) => {
  const dispatch = useDispatch();
  // set up organization specific stuff to be used by the shared hook
  // use the existing hook to get the organizationQuery
  const organizationQuery = useGetOrganizationById(id);
  const sendMutation = (mutatedOrganization) => dispatch(sendUpdateOrganization(mutatedOrganization));
  // return the (now organization specific) hook
  return useMutateResource({ resourceQuery: organizationQuery, sendMutation, onResponse, id });

}


// DELETE

/**
 * Use this hook to access the `sendDeleteOrganization` action
 * 
 * @returns the sendDeleteOrganization action wrapped in dispatch
 * 
 * @example // to use in a component
 * // access the delete action
 * const { sendDeleteOrganization } = useDeleteOrganization();
 * // dispatch the delete action
 * sendDeleteOrganization(organizationId);
 */
export const useDeleteOrganization = () => {
  const dispatch = useDispatch();
  return {
    // return the delete action
    sendDeleteOrganization: (id) => dispatch(sendDeleteOrganization(id))
  }
}

// OTHERS

/**
 * @returns the `addOrganizationToList` action wrapped in dispatch
 */
export const useAddOrganizationToList = () => {
  const dispatch = useDispatch();
  return {
    addOrganizationToList: (organizationId, listArgs) => dispatch(addOrganizationToList({ id: organizationId, queryKey: apiUtils.queryStringFromObject(listArgs) || "all" }))
  }
}

/**
 * NOTE: Only use this if you're sure the organization is already in the store. WILL NOT fetch from the server.
 * @param {string} id - the id of the organization that you want to grab from the store
 * @returns the organization from the store's byId map
 */
export const useOrganizationFromMap = (id) => {
  const organization = useSelector(({ organization: organizationStore }) => selectSingleById(organizationStore, id));
  return organization
}

export const useMyOrgsAndGroups = ({ organizationId }) => {
  const loggedInUser = useLoggedInUser();
  const orgEndpoint = loggedInUser ? myOrganizationsEndpoint : null;
  const groupEndpoint = loggedInUser ? myGroupsEndpoint : null;
  const groupUserEndpoint = loggedInUser ? myGroupUsersEndpoint : null;
  // hold fetches when not logged in to avoid infinite reload loops
  const { data: organizations, ...orgQuery } = useGetOrganizationList(loggedInUser ? orgEndpoint : null);
  const { data: groups, ...groupQuery } = useGetGroupList(groupEndpoint, { status: 'published' });
  const { data: groupUsers, ...groupUserQuery } = useGetGroupUserList(groupUserEndpoint);

  const isLoading = useMemo(() => {
    return orgQuery?.isLoading || groupQuery?.isLoading || groupUserQuery?.isLoading;
  }, [orgQuery?.isLoading, groupQuery?.isLoading, groupUserQuery?.isLoading]);

  return { organizations, groups, groupUsers, isLoading };
}
